import React from 'react';
import { Users, Star, Calendar, Home, Briefcase, HeartHandshake, Clock, Coffee, Activity, Award, UserCheck, Bookmark, User, AlertTriangle } from 'lucide-react';

const GuestsTab = ({ selectedTab, guests, rooms, renderStars, gameDay }) => {
  // Calculate some useful statistics
  const guestCount = guests.length;
  const businessGuests = guests.filter(g => g.type === 'Business').length;
  const familyGuests = guests.filter(g => g.type === 'Family').length;
  const leisureGuests = guests.filter(g => g.type === 'Leisure').length;
  
  // Calculate average happiness
  const avgHappiness = guests.length > 0 
    ? (guests.reduce((sum, guest) => sum + guest.happiness, 0) / guests.length).toFixed(1)
    : 0;
  
  // Get checkouts happening today or tomorrow
  const checkoutsToday = guests.filter(g => g.checkoutDay === gameDay).length;
  const checkoutsTomorrow = guests.filter(g => g.checkoutDay === gameDay + 1).length;
  
  // Helper to get guest type icon
  const getGuestTypeIcon = (type) => {
    switch(type) {
      case 'Business': return <Briefcase className="w-4 h-4" />;
      case 'Family': return <Users className="w-4 h-4" />;
      case 'Leisure': return <Coffee className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };
  
  // Guest type preferences and corresponding amenities
  const guestTypeInfo = {
    Business: {
      icon: <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />,
      color: 'blue',
      preferences: ['Fast WiFi', 'Conference rooms', 'Working desk'],
      amenities: ['Business center', 'Express check-in', 'Productivity amenities']
    },
    Family: {
      icon: <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />,
      color: 'green',
      preferences: ['Large rooms', 'Kid-friendly services', 'Safety features'],
      amenities: ['Swimming pool', 'Game room', 'Family dining options']  
    },
    Leisure: {
      icon: <Coffee className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />,
      color: 'purple',
      preferences: ['Comfort', 'Entertainment', 'Relaxation'],
      amenities: ['Spa services', 'Bar & lounge', 'Local experiences']
    }
  };
  
  // Dynamic empty state component
  const EmptyState = () => (
    <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl">
      <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-gray-200 rounded-full flex items-center justify-center mb-3 sm:mb-4">
        <Users className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
      </div>
      <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2">No Guests Yet</h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto mb-4 px-4">
        Your hotel is currently empty. Keep improving your reputation and facilities to attract guests.
      </p>
      <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-500 transition-colors">
        View Marketing Strategies
      </button>
    </div>
  );
  
  return (
    selectedTab === 'guests' && (
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        {/* Header with guest overview */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-bold flex items-center">
              <Users className="mr-2 text-blue-600" />
              Guest Management
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Manage your hotel's current guests and improve their experience
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* Only show these stats if there are guests */}
            {guestCount > 0 && (
              <>
                <div className="px-3 sm:px-4 py-2 bg-gray-50 rounded-lg flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                    <Award className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Satisfaction</div>
                    <div className="font-bold text-sm sm:text-base flex items-center">
                      {avgHappiness} / 5
                      <Star className="w-3 h-3 text-yellow-400 ml-1 fill-current" />
                    </div>
                  </div>
                </div>
                
                <div className="px-3 sm:px-4 py-2 bg-gray-50 rounded-lg flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center mr-2">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Checkouts</div>
                    <div className="font-bold text-sm sm:text-base">
                      {checkoutsToday > 0 ? (
                        <span className="text-amber-600">{checkoutsToday} today</span>
                      ) : (
                        <span>{checkoutsTomorrow} tomorrow</span>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg flex items-center">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-500 flex items-center justify-center mr-2">
                <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Guests</div>
                <div className="font-bold text-sm sm:text-base">{guestCount} guests</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Guest type breakdown */}
        {guestCount > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {Object.entries(guestTypeInfo).map(([type, info]) => (
              <div key={type} className={`p-3 sm:p-4 rounded-lg bg-${info.color}-50 border border-${info.color}-100`}>
                <div className="flex items-center mb-2 sm:mb-3">
                  {info.icon}
                  <h3 className={`font-medium ml-2 text-sm sm:text-base text-${info.color}-700`}>{type} Guests</h3>
                  <div className={`ml-auto px-2 py-0.5 bg-${info.color}-100 text-${info.color}-800 rounded-full text-xs font-medium`}>
                    {type === 'Business' ? businessGuests : type === 'Family' ? familyGuests : leisureGuests}
                  </div>
                </div>
                <div className={`text-xs text-${info.color}-600`}>
                  <div className="mb-1 font-medium">Typically values:</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {info.preferences.map((pref, i) => (
                      <span key={i} className={`px-1.5 sm:px-2 py-0.5 sm:py-1 bg-${info.color}-100 rounded-full`}>{pref}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Guest list table */}
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <h3 className="font-semibold flex items-center text-sm sm:text-base">
              <Bookmark className="w-4 h-4 mr-2 text-blue-600" />
              Current Guest Register
            </h3>
          </div>
          
          {guests.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-auto max-h-80 sm:max-h-96 rounded-lg border border-gray-200">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="p-2 sm:p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest</th>
                    <th className="p-2 sm:p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accommodation</th>
                    <th className="p-2 sm:p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stay Details</th>
                    <th className="p-2 sm:p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Satisfaction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {guests.map((guest) => {
                    const guestRoom = rooms.find(r => r.id === guest.roomId);
                    const daysLeft = guest.checkoutDay - (gameDay || 1);
                    const isCheckingOutSoon = daysLeft <= 1;
                    
                    return (
                      <tr key={guest.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-2 sm:p-3">
                          <div className="flex items-center">
                            <div 
                              className={`flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center
                                bg-${guest.type === 'Business' ? 'blue' : guest.type === 'Family' ? 'green' : 'purple'}-100`}
                            >
                              {getGuestTypeIcon(guest.type)}
                            </div>
                            <div className="ml-2 sm:ml-3">
                              <div className="font-medium text-gray-900 text-sm sm:text-base">{guest.name}</div>
                              <div className="flex items-center mt-1">
                                <span 
                                  className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs
                                    ${guest.type === 'Business' ? 'bg-blue-100 text-blue-800' :
                                    guest.type === 'Family' ? 'bg-green-100 text-green-800' :
                                    'bg-purple-100 text-purple-800'}`}
                                >
                                  {guest.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 sm:p-3">
                          <div className="flex items-center">
                            <Home className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1" />
                            <span className="font-medium text-sm sm:text-base">Room {guest.roomId}</span>
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 mt-1">
                            {guestRoom?.type || 'Standard'} Room
                          </div>
                        </td>
                        <td className="p-2 sm:p-3">
                          <div className={`flex items-center text-sm ${isCheckingOutSoon ? 'text-amber-600' : 'text-gray-600'}`}>
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            {isCheckingOutSoon ? (
                              daysLeft === 0 ? (
                                <span className="flex items-center font-medium text-xs sm:text-sm">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Checking out today
                                </span>
                              ) : (
                                <span className="font-medium text-xs sm:text-sm">Checking out tomorrow</span>
                              )
                            ) : (
                              <span className="text-xs sm:text-sm">{daysLeft} days left</span>
                            )}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 mt-1">
                            Checkout: Day {guest.checkoutDay}
                          </div>
                        </td>
                        <td className="p-2 sm:p-3">
                          <div>{renderStars(guest.happiness)}</div>
                          <div className="text-xs sm:text-sm text-gray-500 mt-1">
                            {guest.happiness >= 4 ? 'Very satisfied' : 
                             guest.happiness >= 3 ? 'Satisfied' : 
                             guest.happiness >= 2 ? 'Neutral' : 'Unsatisfied'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Tips section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 rounded-lg border border-blue-100">
          <h3 className="font-bold mb-2 sm:mb-3 flex items-center text-blue-800 text-sm sm:text-base">
            <Award className="w-4 h-4 mr-2" />
            Guest Satisfaction Strategy
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h4 className="font-medium mb-1 sm:mb-2 text-blue-700 flex items-center text-xs sm:text-sm">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-yellow-500" />
                Improving Guest Experience
              </h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700">
                <li className="flex items-start">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-100 flex items-center justify-center mr-1 sm:mr-2 mt-0.5">
                    <span className="text-xxs sm:text-xs font-bold text-blue-600">1</span>
                  </div>
                  <span>Happy guests leave good reviews, improving your hotel's reputation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-100 flex items-center justify-center mr-1 sm:mr-2 mt-0.5">
                    <span className="text-xxs sm:text-xs font-bold text-blue-600">2</span>
                  </div>
                  <span>Higher reputation attracts more guests and allows premium pricing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-100 flex items-center justify-center mr-1 sm:mr-2 mt-0.5">
                    <span className="text-xxs sm:text-xs font-bold text-blue-600">3</span>
                  </div>
                  <span>Room quality, staff interaction, and facilities all affect happiness</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-1 sm:mb-2 text-blue-700 flex items-center text-xs sm:text-sm">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-blue-600" />
                Guest Type Preferences
              </h4>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                {Object.entries(guestTypeInfo).map(([type, info]) => (
                  <div key={type} className={`flex items-start`}>
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-${info.color}-100 flex items-center justify-center mr-1 sm:mr-2 mt-0.5`}>
                      {getGuestTypeIcon(type)}
                    </div>
                    <div>
                      <span className="font-medium">{type}:</span> {info.amenities.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default GuestsTab;